//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';

import createUUID from '@utils/uuid';
import PinId from '@models/PinId';
import {
  IBulkPinScheduleDetails,
  IPinterestPostDetailsPin,
  MAX_PIN_DESCRIPTION_LENGTH,
  MAX_PIN_TITLE_LENGTH,
  MAX_VIDEO_DURATION_MILLIS,
  MIN_MEDIA_HEIGHT_PX,
  MIN_MEDIA_WIDTH_PX,
  PinterestPostDetailsBulkScheduleValidationError,
  PinterestPostDetailsValidationError,
} from '@pinterest/redux/post-details/pinterestPostDetailsModel';
import { isValidUrlWithProtocol } from '@utils/urlValidation';
import { isDefined } from '@utils/filterPredicates';
import PinterestBoardId from '@models/PinterestBoardId';
import { getMediaMetadata, IMlsMediaItem } from '@models/IMlsMediaItem';
import Cuid from '@models/Cuid';

export const generateEmptyPin = (): IPinterestPostDetailsPin => {
  return {
    id: PinId(createUUID()),
    cuids: [],
    validationErrors: PojoSet.empty(),
    invalidCuids: PojoSet.empty(),
  };
};

export const validatePinMediaSpecs = (mediaItems: IMlsMediaItem[]) => {
  let validationErrors = PojoSet.empty<PinterestPostDetailsValidationError>();
  let invalidCuids = PojoSet.empty<Cuid>();

  mediaItems.forEach((mediaItem) => {
    const cuid = mediaItem.cuid;
    const mediaMetadata = getMediaMetadata(mediaItem);

    if (
      (mediaMetadata?.width ?? 0) < MIN_MEDIA_WIDTH_PX ||
      (mediaMetadata?.height ?? 0) < MIN_MEDIA_HEIGHT_PX
    ) {
      validationErrors = PojoSet.add(
        validationErrors,
        PinterestPostDetailsValidationError.MediaTooSmall,
      );
      invalidCuids = PojoSet.add(invalidCuids, cuid);
    }
    if ((mediaMetadata?.durationMillis ?? 0) > MAX_VIDEO_DURATION_MILLIS) {
      validationErrors = PojoSet.add(
        validationErrors,
        PinterestPostDetailsValidationError.VideoTooLong,
      );
      invalidCuids = PojoSet.add(invalidCuids, cuid);
    }
  });

  return {
    invalidCuids: invalidCuids,
    validationErrors: validationErrors,
  };
};

export const validatePinMediaAndDetails = (
  pin: IPinterestPostDetailsPin,
  mediaItems: IMlsMediaItem[],
): {
  formErrors: PojoSet<PinterestPostDetailsValidationError>;
  invalidCuids: PojoSet<Cuid>;
} => {
  const { cuids, boardId, description, destinationLink, title } = pin;

  let formErrors = PojoSet.empty<PinterestPostDetailsValidationError>();
  let invalidCuids = PojoSet.empty<Cuid>();
  if (cuids.length < 1) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsValidationError.NoMedia,
    );
  } else {
    const { validationErrors, invalidCuids: invalidMediaCuids } =
      validatePinMediaSpecs(mediaItems);
    formErrors = PojoSet.union(formErrors, validationErrors);
    invalidCuids = PojoSet.union(invalidCuids, invalidMediaCuids);
  }

  if (!boardId) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsValidationError.NoBoard,
    );
  }

  if ((description?.length ?? 0) > MAX_PIN_DESCRIPTION_LENGTH) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsValidationError.DescriptionTooLong,
    );
  }

  if (isDefined(destinationLink) && !isValidUrlWithProtocol(destinationLink)) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsValidationError.InvalidDestinationLink,
    );
  }

  if ((title?.length ?? 0) > MAX_PIN_TITLE_LENGTH) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsValidationError.TitleTooLong,
    );
  }

  return {
    formErrors,
    invalidCuids,
  };
};

export const validateSinglePinPostDetails = (
  pin: IPinterestPostDetailsPin,
  mediaItems: IMlsMediaItem[],
): {
  formErrors: PojoSet<PinterestPostDetailsValidationError>;
  invalidCuids: PojoSet<Cuid>;
} => {
  let singlePinFormErrors =
    PojoSet.empty<PinterestPostDetailsValidationError>();

  const { formErrors, invalidCuids } = validatePinMediaAndDetails(
    pin,
    mediaItems,
  );

  singlePinFormErrors = PojoSet.union(singlePinFormErrors, formErrors);

  if (!isDefined(pin.scheduledTime)) {
    singlePinFormErrors = PojoSet.add(
      singlePinFormErrors,
      PinterestPostDetailsValidationError.NoPostTime,
    );
  } else if (pin.scheduledTime < new Date()) {
    singlePinFormErrors = PojoSet.add(
      singlePinFormErrors,
      PinterestPostDetailsValidationError.PostTimeInPast,
    );
  }

  return {
    formErrors: singlePinFormErrors,
    invalidCuids,
  };
};

export const validateBulkPinMediaAndDetails = (
  pins: IPinterestPostDetailsPin[],
  scheduleMedia: PojoMap<Cuid, IMlsMediaItem>,
): PojoMap<
  PinId,
  {
    formErrors: PojoSet<PinterestPostDetailsValidationError>;
    invalidCuids: PojoSet<Cuid>;
  }
> => {
  return PojoMap.fromEntries(
    pins.map((pin) => {
      const associatedMediaItems = pin.cuids
        .map((c) => PojoMap.get(scheduleMedia, c))
        .filter(isDefined);
      const formErrors = validatePinMediaAndDetails(pin, associatedMediaItems);

      return [pin.id, formErrors];
    }),
  );
};

export const validateBulkPinBoardSelect = (
  isBulkBoardSelectEnabled: boolean,
  bulkBoardSelectBoardId?: PinterestBoardId,
): PojoSet<PinterestPostDetailsBulkScheduleValidationError> => {
  let formErrors =
    PojoSet.empty<PinterestPostDetailsBulkScheduleValidationError>();

  if (isBulkBoardSelectEnabled && !bulkBoardSelectBoardId) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsBulkScheduleValidationError.NoBulkBoardSelected,
    );
  }

  return formErrors;
};

export const validateBulkScheduleDetails = (
  bulkScheduleDetails: IBulkPinScheduleDetails,
): PojoSet<PinterestPostDetailsBulkScheduleValidationError> => {
  const { daysOfWeek, pinsPerDay, postingInterval, startScheduledTime } =
    bulkScheduleDetails;

  let formErrors =
    PojoSet.empty<PinterestPostDetailsBulkScheduleValidationError>();
  if (daysOfWeek.length === 0) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsBulkScheduleValidationError.NoDayOfWeek,
    );
  }

  if (!isDefined(pinsPerDay)) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsBulkScheduleValidationError.NoPinsPerDay,
    );
  } else if (pinsPerDay > 1 && !isDefined(postingInterval)) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsBulkScheduleValidationError.NoPostingInterval,
    );
  }

  if (!isDefined(startScheduledTime)) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsBulkScheduleValidationError.NoStartPostTime,
    );
  } else if (startScheduledTime < new Date()) {
    formErrors = PojoSet.add(
      formErrors,
      PinterestPostDetailsBulkScheduleValidationError.StartPostTimeInPast,
    );
  }

  return formErrors;
};
